import { format } from 'date-fns';
import { DownloadIcon } from '@dynamika-shared/ui/icons';
import { Spinner } from '@dynamika-shared/ui/Spinner/Spinner';
import { Tooltip } from '@dynamika-shared/ui/Tooltip/Tooltip';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { convertDataSize } from '@utils/convertDataSize';
import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import {
  ExternalUserFile,
  externalUserFileModuleMap,
  SelectedFile,
} from '@restore/ExternalUserSubjects/ExternalUserSubjects.types';

export const makeVisitFileListColumns = (
  onDownload: (fileId: string) => void,
  downloadingFileIds: Set<string>
): ColumnType<ExternalUserFile>[] => [
  {
    name: 'source',
    header: () => 'Source',
    thClassName: 'w-[40%] min-w-[40%] max-w-[40%] border-t-0',
    headerClassName: '!pl-5',
    cellClassName: 'pl-5',
    loading: () => <div className="h-2 w-10 animate-pulse rounded-md bg-slate-300"></div>,
    getValue: row => externalUserFileModuleMap[row.module] ?? '-',
  },
  {
    name: 'dataType',
    header: () => 'File Type',
    thClassName: 'w-[18%] min-w-[18%] max-w-[18%]',
    loading: () => <div className="h-2 w-10 animate-pulse rounded-md bg-slate-300"></div>,
    getValue: row => row.dataType,
  },
  {
    name: 'created',
    header: () => 'Created Date',
    hasSorting: true,
    loading: () => <div className="h-2 w-10 animate-pulse rounded-md bg-slate-300"></div>,
    getValue: row => {
      if (!row.created) return '-';

      return format(new Date(row.created), 'dd-MMM-yyyy');
    },
  },
  {
    name: 'action',
    header: () => 'Action',
    loading: () => <div className="h-2 w-10 animate-pulse rounded-md bg-slate-300"></div>,
    getValue: row => {
      const isDownloading = downloadingFileIds.has(row.id);
      return (
        <button
          onClick={() => !isDownloading && onDownload(row.id)}
          aria-label='Download file'
          disabled={isDownloading}
        >
          {isDownloading ? (
            <Spinner
              className='!mr-0 h-4 w-4'
              spinCircleColor='#F26E23'
              spinColor='#FFF6F0'
            />
          ) : (
            <DownloadIcon
              width={24}
              height={24}
              className='cursor-pointer text-pastelBlue transition-colors hover:text-dodgerBlue'
            />
          )}
        </button>
      );
    },
    alignToRight: true,
  },
];

export const makeVisitFileListUploadedReportsColumns = (
  onCheck: (file: SelectedFile) => void,
  selectedFiles: SelectedFile[]
): ColumnType<ExternalUserFile>[] => [
  {
    name: 'name',
    header: () => 'File Name',
    thClassName: 'w-[40%] min-w-[40%] max-w-[40%] border-t-0',
    headerClassName: '!pl-5',
    cellClassName: 'pl-5',
    loading: () => <div className="h-2 w-10 animate-pulse rounded-md bg-slate-300"></div>,
    getValue: row => <Tooltip title={row.name}>{row.name}</Tooltip>,
  },
  {
    name: 'size',
    header: () => 'File Size',
    thClassName: 'w-[18%] min-w-[18%] max-w-[18%]',
    loading: () => <div className="h-2 w-10 animate-pulse rounded-md bg-slate-300"></div>,
    getValue: row => convertDataSize(row.size),
  },
  {
    name: 'created',
    header: () => 'Created Date',
    hasSorting: true,
    thClassName: 'w-[18%] min-w-[18%] max-w-[18%]',
    loading: () => <div className="h-2 w-10 animate-pulse rounded-md bg-slate-300"></div>,
    getValue: row => {
      if (!row.created) return '-';

      return format(new Date(row.created), 'dd-MMM-yyyy');
    },
  },
  {
    name: 'action',
    header: () => 'Action',
    loading: () => <div className="h-2 w-10 animate-pulse rounded-md bg-slate-300"></div>,
    getValue: row => (
      <Checkbox
        className="p-0.5"
        onChange={() => onCheck({ id: row.id, name: row.name, size: row.size })}
        checked={selectedFiles.some(f => f.id === row.id)}
      />
    ),
    alignToRight: true,
  },
];
